import {
  forwardRef,
  InputHTMLAttributes,
  Ref,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { YearlessDateMode, YearlessDate } from "../types";
import { YearlessDateSelectionPopover } from "./YearlessDateSelectionPopover";
import { DateFieldYearlessChangeHandler } from "../DateFieldYearless";
import {
  YearlessDateInput,
  YearlessDateInputChange,
  YearlessDateInputRef,
} from "./YearlessDateInput";
import { validateYearlessDate } from "./utils";
import {
  useFocusWithin,
  useOptionallyControlledState,
} from "../../../internal";
import { Size } from "../../../types";
import { mergeRefs } from "../../../hooks/useMergeRefs/useMergeRefs";

export type YearlessDateInputWithPickerProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "onChange" | "value" | "ref" | "size" | "defaultValue" | "onFocus" | "onBlur"
> & {
  /**
   * The controlled value of the date field
   */
  value?: YearlessDate | null;
  /**
   * The default value for uncontrolled usage
   */
  defaultValue?: YearlessDate | null;
  /**
   * The function to call when the date value changes
   */
  onChange?: DateFieldYearlessChangeHandler;
  /**
   * The date format mode (mm/dd or dd/mm)
   */
  mode?: YearlessDateMode;
  /**
   * Whether the field is required
   */
  required?: boolean;
  /**
   * The minimum allowed date
   */
  minDate?: YearlessDate | null;
  /**
   * The maximum allowed date
   */
  maxDate?: YearlessDate | null;
  /**
   * Configuration for unavailable dates
   */
  unavailable?: {
    /**
     * Array of dates that are not selectable
     */
    dates?: YearlessDate[];
  };
  /**
   * Whether the picker is disabled
   */
  disablePicker?: boolean;
  /**
   * The size of the input
   */
  size?: Extract<Size, "small" | "medium" | "large">;
  /**
   * Whether the input has an error
   */
  error?: boolean;
  /**
   * Whether the input is in loading state
   */
  loading?: boolean;
  /**
   * Function to call when the input is focused
   */
  onFocus?: (event: React.FocusEvent) => void;
  /**
   * Function to call when the input is blurred
   */
  onBlur?: (event: React.FocusEvent) => void;
};

export type YearlessDateInputWithPickerRef = {
  focus: () => void;
  setValue: (value: YearlessDate | null) => void;
};

export const YearlessDateInputWithPicker = forwardRef<
  YearlessDateInputWithPickerRef,
  YearlessDateInputWithPickerProps
>((props, ref) => {
  const {
    value: valueProp,
    defaultValue: defaultValueProp,
    onChange: onChangeProp,
    minDate,
    maxDate,
    unavailable,
    required,
    disablePicker = false,
    mode = "mm/dd",
    size = "medium",
    error = false,
    loading = false,
    onFocus: onFocusProp,
    onBlur: onBlurProp,
    ...restProps
  } = props;

  const inputRef = useRef<YearlessDateInputRef>(null);
  const popoverRef = useRef<{
    openPopover: () => void;
    closePopover: () => void;
  }>(null);
  const popoverContentRef = useRef<HTMLDivElement>(null);

  const [value, setValue] = useOptionallyControlledState<YearlessDate | null>({
    controlledValue: valueProp,
    defaultValue: defaultValueProp,
  });
  const [tempPickerValue, setTempPickerValue] = useState<YearlessDate | null>(
    value,
  );

  useImperativeHandle(
    ref,
    (): YearlessDateInputWithPickerRef => ({
      focus: () => {
        if (!inputRef.current) return;
        return inputRef.current.focus();
      },
      setValue: (value: YearlessDate | null) => {
        if (!inputRef.current) return;
        inputRef.current.setValue(value);
      },
    }),
    [inputRef],
  );

  const currentValidity = useMemo(
    () =>
      validateYearlessDate({
        value: value ?? null,
        constraints: {
          required: required,
          unavailable: unavailable,
          minDate: minDate,
          maxDate: maxDate,
        },
      }),
    [value, required, unavailable, minDate, maxDate],
  );

  const handleInputChange = (change: YearlessDateInputChange) => {
    const { event, value, ...restChange } = change;
    setValue(value);
    setTempPickerValue(value);
    onChangeProp?.({
      ...restChange,
      value,
      isValid: validateYearlessDate({
        value,
        constraints: {
          required: required,
          unavailable: unavailable,
          minDate: minDate,
          maxDate: maxDate,
        },
      }),
    });
  };

  const inputProps = {
    required,
    mode,
    autoComplete: "off",
    ...restProps,
    value: value,
    onChange: handleInputChange,
    "data-valid": currentValidity,
    size,
    error,
    loading,
  };

  const { props: focusWithinProps } = useFocusWithin({
    onFocus: (e) => {
      requestAnimationFrame(() => {
        onFocusProp?.(e);
      });
    },
    onBlur: (e) => {
      requestAnimationFrame(() => {
        onBlurProp?.(e);
      });
      popoverRef.current?.closePopover();
    },
    otherRelatedElements: popoverContentRef?.current
      ? [popoverContentRef.current]
      : [],
  });

  if (disablePicker) {
    return <YearlessDateInput ref={inputRef} {...inputProps} />;
  }

  const handlePickerChange = (newValue: YearlessDate) => {
    setTempPickerValue(newValue);
    if (!inputRef.current) return;
    if (
      (mode === "mm/dd" && newValue.month) ||
      (mode === "dd/mm" && newValue.day)
    ) {
      const valueSet = inputRef.current.setValue(newValue);
      setValue(valueSet.value);
      onChangeProp?.({
        ...valueSet,
        isValid: validateYearlessDate({
          value: valueSet.value,
          constraints: {
            required: required,
            unavailable: unavailable,
            minDate: minDate,
            maxDate: maxDate,
          },
        }),
      });
    }
  };

  const handlePickerMonthChange = (month: number | null) => {
    handlePickerChange({
      day: value?.day ?? tempPickerValue?.day ?? null,
      month,
    });
  };

  const handlePickerDayChange = (day: number | null) => {
    handlePickerChange({
      day,
      month: value?.month ?? tempPickerValue?.month ?? null,
    });
  };

  const handlePickerClose = () => {
    setTempPickerValue(null);
  };

  const handleInputFocus = (event: React.FocusEvent) => {
    focusWithinProps.onFocus?.(event as React.FocusEvent<HTMLElement>);
  };
  const handleInputBlur = (event: React.FocusEvent) => {
    focusWithinProps.onBlur?.(event as React.FocusEvent<HTMLElement>);
  };

  const handlePopoverBlur = (event: React.FocusEvent) => {
    focusWithinProps.onBlur?.(event as React.FocusEvent<HTMLElement>);
  };

  return (
    <YearlessDateSelectionPopover
      popoverRef={popoverRef}
      popoverContentRef={popoverContentRef}
      month={value?.month ?? tempPickerValue?.month ?? null}
      day={value?.day ?? tempPickerValue?.day ?? null}
      onMonthChange={handlePickerMonthChange}
      onDayChange={handlePickerDayChange}
      onClose={handlePickerClose}
      mode={mode}
      onBlur={handlePopoverBlur}
    >
      {({ ref, onKeyDown }) => (
        <YearlessDateInput
          {...inputProps}
          ref={mergeRefs([
            inputRef,
            ref as unknown as Ref<YearlessDateInputRef>,
          ])}
          onKeyDown={(ev) => {
            onKeyDown?.(ev);
            inputProps.onKeyDown?.(ev);
          }}
          onClick={(ev) => {
            inputProps.onClick?.(ev);
            popoverRef.current?.openPopover();
          }}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          enableStepping
        />
      )}
    </YearlessDateSelectionPopover>
  );
});

YearlessDateInputWithPicker.displayName = "YearlessDateInputWithPicker";
